import { MoonIcon, SunIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { useT } from "@/i18n";
import { chooseTheme, THEMES, useTheme } from "@/lib/ground";

/**
 * The device's theme for the glass ground (`src/lib/ground.ts`): dark, the
 * broadcast identity, or light.
 *
 * Two chips in the language switcher's shape, in the phone Menu and on the
 * doors, where a row has room for the words. The desktop header pill has
 * not: SCORE, four links, the account door and the language chips already
 * share it at 960px, and the account door can carry a name. There the
 * switcher is ONE icon button (`compact`) offering the other theme - the sun
 * while it is dark, the moon while it is light - and says so in words to a
 * screen reader and in its tooltip.
 */
export function ThemeSwitcher({
  className,
  compact = false,
}: {
  className?: string | undefined;
  compact?: boolean;
}) {
  const theme = useTheme();
  const t = useT();
  if (compact) {
    const next: (typeof THEMES)[number] = theme === "dark" ? "light" : "dark";
    const label = next === "light" ? t("theme.toLight") : t("theme.toDark");
    return (
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={label}
        title={label}
        className={className}
        onClick={() => chooseTheme(next)}
      >
        {next === "light" ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
      </Button>
    );
  }
  return (
    <ButtonGroup aria-label={t("theme.label")} className={className}>
      {THEMES.map((code) => {
        const on = code === theme;
        return (
          <Button
            key={code}
            size="sm"
            variant={on ? "default" : "outline"}
            aria-pressed={on}
            onClick={() => chooseTheme(code)}
          >
            {code === "dark" ? t("theme.dark") : t("theme.light")}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
